/**
 * 🏆 useAchievements Hook
 * 업적/배지 시스템을 위한 커스텀 훅
 * localStorage와 Firestore를 동시에 지원합니다
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { BADGES, getBadgeById, getRarityConfig } from '../constants/badges';
import { db, auth } from '../firebase';
import { doc, getDoc, setDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';
import confetti from 'canvas-confetti';

const LOCAL_STORAGE_KEY_PREFIX = 'thestaff_achievements_';

export default function useAchievements(userProfile) {
    // 상태
    const [earnedBadges, setEarnedBadges] = useState([]);
    const [stats, setStats] = useState({
        student_count: 0,
        teacher_count: 0,
        visit_count: 0,
        attendance_count: 0,
        schedule_count: 0,
        finance_count: 0,
        streak_days: 0,
        last_access_date: null,
        first_access_date: null,
        visit_type_counts: {},
        birthday_view_count: 0,
        birthday_visit_count: 0
    });
    const [newBadge, setNewBadge] = useState(null);
    const [showBadgeModal, setShowBadgeModal] = useState(false);

    // [NEW] 초기 로딩 완료 상태
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);

    // 초기 데이터 로드 - auth.currentUser가 존재할 때 실행
    useEffect(() => {
        // 로그인 확인 (userProfile은 optional - 있으면 더 좋고, 없어도 기본 동작)
        if (auth.currentUser) {
            const initializeAchievements = async () => {
                await loadData();
                checkStreak();

                // [FIX] 화면 로딩 완료 후 2초 딜레이 뒤에 특별 날짜 배지 체크
                // userProfile이 로드된 후에만 실행하도록 추가 조건
                if (userProfile) {
                    setTimeout(() => {
                        setIsInitialLoadComplete(true);
                        checkSpecialDates();
                    }, 2000);
                }
            };

            initializeAchievements();
        } else {
            // 로그아웃 시 상태 초기화
            setIsInitialLoadComplete(false);
            setEarnedBadges([]);
            setStats({
                student_count: 0,
                teacher_count: 0,
                visit_count: 0,
                attendance_count: 0,
                schedule_count: 0,
                finance_count: 0,
                streak_days: 0,
                last_access_date: null,
                first_access_date: null,
                visit_type_counts: {},
                birthday_view_count: 0,
                birthday_visit_count: 0
            });
        }
    }, [auth.currentUser?.uid, userProfile]);

    // 데이터 로드 (localStorage + Firestore)
    const loadData = async () => {
        const currentUid = auth.currentUser?.uid;
        if (!currentUid) return;

        const storageKey = LOCAL_STORAGE_KEY_PREFIX + currentUid;

        // 1. localStorage 먼저 로드
        try {
            const localData = localStorage.getItem(storageKey);
            if (localData) {
                const parsed = JSON.parse(localData);
                console.log('[Achievement] localStorage 로드:', parsed.earnedBadges); // [DEBUG]
                setEarnedBadges(parsed.earnedBadges || []);
                setStats(prev => ({ ...prev, ...parsed.stats }));
            }
        } catch (e) {
            console.error('Local achievement load error:', e);
        }

        // 2. Firestore에서 로드 (로그인된 경우)
        if (auth.currentUser && !auth.currentUser.isAnonymous) {
            try {
                const docRef = doc(db, 'userAchievements', auth.currentUser.uid);
                const docSnap = await getDoc(docRef);

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    console.log('[Achievement] Firestore 로드:', data.earnedBadges); // [DEBUG]
                    setEarnedBadges(data.earnedBadges || []);
                    setStats(prev => ({ ...prev, ...data.stats }));
                    // localStorage 동기화
                    saveToLocal({ earnedBadges: data.earnedBadges, stats: data.stats });
                } else {
                    console.log('[Achievement] Firestore 문서 없음 - 새 사용자'); // [DEBUG]
                }
            } catch (error) {
                console.error('Firestore achievement load error:', error);
            }
        }
    };

    // localStorage 저장
    const saveToLocal = (data) => {
        const currentUid = auth.currentUser?.uid;
        if (!currentUid) return;

        try {
            localStorage.setItem(LOCAL_STORAGE_KEY_PREFIX + currentUid, JSON.stringify(data));
        } catch (e) {
            console.error('Local save error:', e);
        }
    };

    // [OPTIMIZED] 🚀 Firestore 최적화: getDoc 제거, setDoc merge 사용
    // 기존: 저장 전 getDoc으로 문서 존재 여부 확인 (불필요한 읽기 발생)
    // 변경: setDoc의 merge 옵션으로 존재 여부 상관없이 바로 저장 (읽기 0회)
    const saveToFirestore = async (updates) => {
        if (!auth.currentUser || auth.currentUser.isAnonymous) return;

        try {
            const docRef = doc(db, 'userAchievements', auth.currentUser.uid);

            // merge: true 옵션으로 기존 데이터 유지하면서 업데이트
            // 문서가 없으면 자동 생성, 있으면 병합
            await setDoc(docRef, {
                ...updates,
                organizationId: userProfile?.organizationId || null,
                updatedAt: serverTimestamp()
            }, { merge: true });

        } catch (error) {
            console.error('Firestore save error:', error);
        }
    };

    // 접속 스트릭 체크
    const checkStreak = useCallback(() => {
        const today = new Date().toISOString().split('T')[0];
        const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];

        setStats(prev => {
            let newStreak = prev.streak_days || 0;
            const lastAccess = prev.last_access_date;

            if (lastAccess === today) {
                return prev; // 이미 오늘 접속함
            } else if (lastAccess === yesterday) {
                newStreak += 1; // 연속 접속
            } else if (lastAccess) {
                newStreak = 1; // 연속 끊김
            } else {
                newStreak = 1; // 첫 접속
            }

            const newStats = {
                ...prev,
                streak_days: newStreak,
                last_access_date: today,
                first_access_date: prev.first_access_date || today
            };

            // 저장
            saveToLocal({ earnedBadges, stats: newStats });
            saveToFirestore({ stats: newStats });

            return newStats;
        });
    }, [earnedBadges]);

    // [NEW] 부활절 날짜 계산 (간이 알고리즘)
    const getEasterDate = (year) => {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31);
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        return { month, day };
    };

    // [NEW] 추수감사절 날짜 (한국 교회 기준: 11월 셋째 주 일요일)
    const getThanksgivingDate = (year) => {
        const nov1 = new Date(year, 10, 1); // 11월 1일
        const dayOfWeek = nov1.getDay(); // 0(일) ~ 6(토)
        const firstSunday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek);
        const thirdSunday = firstSunday + 14;
        return { month: 11, day: thirdSunday };
    };

    // 특별 날짜 체크 (성탄절, 새벽, 밤 등) + 부활절/추수감사절 추가
    const checkSpecialDates = useCallback(() => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const day = now.getDate();
        const hour = now.getHours();

        // 새벽 기도자 (5시 이전)
        if (hour < 5) {
            awardBadge('early_bird');
        }

        // 밤의 목양자 (23시 이후)
        if (hour >= 23) {
            awardBadge('night_owl');
        }

        // 성탄절
        if (month === 12 && day === 25) {
            awardBadge('christmas');
        }

        // 부활절
        const easter = getEasterDate(year);
        if (month === easter.month && day === easter.day) {
            awardBadge('easter');
        }

        // 추수감사절
        const thanksgiving = getThanksgivingDate(year);
        if (month === thanksgiving.month && day === thanksgiving.day) {
            awardBadge('thanksgiving');
        }
    }, []);

    // 통계 증가
    const incrementStat = useCallback((statKey, amount = 1, subKey = null) => {
        setStats(prev => {
            const newStats = { ...prev };

            if (subKey) {
                if (!newStats[statKey]) newStats[statKey] = {};
                newStats[statKey][subKey] = (newStats[statKey][subKey] || 0) + amount;
            } else {
                newStats[statKey] = (newStats[statKey] || 0) + amount;
            }

            // [NEW] 자정 심방 체크 (night_shepherd)
            if (statKey === 'visit_count') {
                const hour = new Date().getHours();
                if (hour === 0) { // 00시 ~ 01시 사이
                    newStats.midnight_visit_count = (newStats.midnight_visit_count || 0) + 1;
                }
            }

            // [NEW] 동시 출석 체크 (speedster) - amount가 10 이상이면
            if (statKey === 'attendance_count' && amount >= 10) {
                newStats.max_bulk_attendance = Math.max(newStats.max_bulk_attendance || 0, amount);
            }

            // 저장
            saveToLocal({ earnedBadges, stats: newStats });
            saveToFirestore({ [`stats.${statKey}${subKey ? `.${subKey}` : ''}`]: newStats[statKey] });

            // 특수 통계 업데이트가 있다면 그것도 저장
            if (newStats.midnight_visit_count !== prev.midnight_visit_count) {
                saveToFirestore({ 'stats.midnight_visit_count': newStats.midnight_visit_count });
            }
            if (newStats.max_bulk_attendance !== prev.max_bulk_attendance) {
                saveToFirestore({ 'stats.max_bulk_attendance': newStats.max_bulk_attendance });
            }

            return newStats;
        });
    }, [earnedBadges]);



    // 배지 수여 - [FIX] 함수형 업데이트로 race condition 방지
    const awardBadge = useCallback((badgeId) => {
        const badge = getBadgeById(badgeId);
        if (!badge) return false;

        // [FIX] 함수형 업데이트로 현재 상태를 안전하게 참조
        setEarnedBadges(prevBadges => {
            // 이미 획득한 배지면 스킵 (모달도 표시하지 않음)
            if (prevBadges.includes(badgeId)) {
                return prevBadges;
            }

            const newEarnedBadges = [...prevBadges, badgeId];

            // 저장 (함수 내에서 호출)
            saveToLocal({ earnedBadges: newEarnedBadges, stats });
            saveToFirestore({
                earnedBadges: arrayUnion(badgeId),
                [`badgeEarnedAt.${badgeId}`]: serverTimestamp()
            });

            console.log('[Achievement] 새 배지 획득!:', badgeId);

            // [FIX] 모달 표시 - setter 내부에서 호출하여 새 배지일 때만 표시
            setTimeout(() => {
                setNewBadge(badge);
                setShowBadgeModal(true);
                triggerBadgeConfetti(badge.rarity);
            }, 100);

            return newEarnedBadges;
        });

        return true;
    }, [stats]);

    // 모든 배지 조건 체크
    const checkAndAwardBadges = useCallback(() => {
        // [NEW] 사용 기간 계산
        let usageDays = 0;
        if (stats.first_access_date) {
            const start = new Date(stats.first_access_date);
            const now = new Date();
            const diff = now - start;
            usageDays = Math.floor(diff / (1000 * 60 * 60 * 24));
        }

        BADGES.forEach(badge => {
            if (earnedBadges.includes(badge.id)) return;

            const { condition } = badge;
            let earned = false;

            switch (condition.type) {
                // 기존 카운트류
                case 'student_count': earned = Number(stats.student_count || 0) >= condition.value; break;
                case 'teacher_count': earned = Number(stats.teacher_count || 0) >= condition.value; break;
                case 'visit_count': earned = Number(stats.visit_count || 0) >= condition.value; break;
                case 'attendance_count': earned = Number(stats.attendance_count || 0) >= condition.value; break;
                case 'schedule_count': earned = Number(stats.schedule_count || 0) >= condition.value; break;
                case 'finance_count': earned = Number(stats.finance_count || 0) >= condition.value; break;
                case 'streak_days': earned = Number(stats.streak_days || 0) >= condition.value; break;

                // 심방 유형
                case 'visit_type_count':
                    const types = Array.isArray(condition.visitType) ? condition.visitType : [condition.visitType];
                    const totalTypeCount = types.reduce((sum, type) => sum + Number(stats.visit_type_counts?.[type] || 0), 0);
                    earned = totalTypeCount >= condition.value;
                    break;

                // 생일 관련
                case 'birthday_view_count': earned = Number(stats.birthday_view_count || 0) >= condition.value; break;
                case 'birthday_visit_count': earned = Number(stats.birthday_visit_count || 0) >= condition.value; break;

                // 격려 관련
                case 'cheer_sent_count': earned = Number(stats.cheer_sent_count || 0) >= condition.value; break;

                // [NEW] 새로 구현된 로직들
                case 'usage_days': // one_year, two_years
                    earned = usageDays >= condition.value;
                    break;

                case 'single_student_visits': // devoted_shepherd
                    earned = Number(stats.max_single_student_visits || 0) >= condition.value;
                    break;

                case 'bulk_attendance': // speedster
                    earned = Number(stats.max_bulk_attendance || 0) >= condition.value;
                    break;

                case 'midnight_visit': // night_shepherd
                    earned = Number(stats.midnight_visit_count || 0) >= 1;
                    break;

                case 'category_complete': // completionist_first
                    // 해당 카테고리의 모든 배지(자신 제외)를 획득했는지 확인
                    const categoryBadges = BADGES.filter(b => b.category === condition.category && b.id !== badge.id);
                    const allEarned = categoryBadges.every(b => earnedBadges.includes(b.id));
                    earned = allEarned;
                    break;

                case 'budget_success': // budget_keeper
                    // 연말(12/31)에 체크하거나, syncWithExistingData에서 예산 준수 여부 플래그 확인
                    earned = (stats.budget_success_years || 0) >= condition.value;
                    break;

                case 'team_created_with_invite': // team_leader
                    earned = !!userProfile?.organizationId && (stats.invite_count || 0) >= 1;
                    break;

                default:
                    break;
            }

            if (earned) {
                awardBadge(badge.id);
            }
        });
    }, [earnedBadges, stats, awardBadge, userProfile]);

    // Confetti 효과
    const triggerBadgeConfetti = (rarity) => {
        const colors = {
            common: ['#9CA3AF', '#D1D5DB'],
            uncommon: ['#10B981', '#34D399'],
            rare: ['#3B82F6', '#60A5FA'],
            epic: ['#8B5CF6', '#A78BFA'],
            legendary: ['#F59E0B', '#FBBF24', '#FCD34D']
        };

        confetti({
            particleCount: rarity === 'legendary' ? 200 : rarity === 'epic' ? 150 : 100,
            spread: rarity === 'legendary' ? 120 : 70,
            colors: colors[rarity] || colors.common,
            origin: { y: 0.6 }
        });

        if (rarity === 'legendary') {
            setTimeout(() => {
                confetti({
                    particleCount: 100,
                    angle: 60,
                    spread: 55,
                    origin: { x: 0 },
                    colors: colors.legendary
                });
                confetti({
                    particleCount: 100,
                    angle: 120,
                    spread: 55,
                    origin: { x: 1 },
                    colors: colors.legendary
                });
            }, 200);
        }
    };

    // 총 포인트
    const totalPoints = useMemo(() => {
        return earnedBadges.reduce((sum, badgeId) => {
            const badge = getBadgeById(badgeId);
            return sum + (badge?.points || 0);
        }, 0);
    }, [earnedBadges]);

    // 레벨 계산
    const level = useMemo(() => {
        if (totalPoints < 100) return 1;
        if (totalPoints < 300) return 2;
        if (totalPoints < 600) return 3;
        if (totalPoints < 1000) return 4;
        if (totalPoints < 1500) return 5;
        if (totalPoints < 2500) return 6;
        if (totalPoints < 4000) return 7;
        if (totalPoints < 6000) return 8;
        if (totalPoints < 9000) return 9;
        return 10;
    }, [totalPoints]);

    // 레벨 진행률
    const levelProgress = useMemo(() => {
        const thresholds = [0, 100, 300, 600, 1000, 1500, 2500, 4000, 6000, 9000, Infinity];
        const currentLevelPoints = thresholds[level - 1];
        const nextLevelPoints = thresholds[level];
        const progress = ((totalPoints - currentLevelPoints) / (nextLevelPoints - currentLevelPoints)) * 100;
        return Math.min(100, Math.max(0, progress));
    }, [totalPoints, level]);

    // 배지 모달 닫기
    const closeBadgeModal = useCallback(() => {
        setShowBadgeModal(false);
        setNewBadge(null);
    }, []);

    // [MODIFIED] 기존 데이터와 통계 동기화 함수
    const syncWithExistingData = useCallback((data) => {
        const { students = [], teachers = [], visitLogs = [], schedules = [], finances = [] } = data;

        const currentUserId = auth.currentUser?.uid;
        if (!currentUserId) return;

        const myStudents = students.filter(s => s.userId === currentUserId);
        const myTeachers = teachers.filter(t => t.userId === currentUserId);
        const myVisitLogs = visitLogs.filter(v => v.userId === currentUserId);
        const mySchedules = schedules.filter(s => s.userId === currentUserId);
        const myFinances = finances.filter(f => f.userId === currentUserId);

        // [NEW] 한 학생 최다 심방 횟수 계산
        const visitsByStudent = {};
        myVisitLogs.forEach(log => {
            if (log.studentId) {
                visitsByStudent[log.studentId] = (visitsByStudent[log.studentId] || 0) + 1;
            }
        });
        const maxSingleStudentVisits = Object.values(visitsByStudent).reduce((max, curr) => Math.max(max, curr), 0);

        // [NEW] 자정 심방 카운트
        const midnightVisitCount = myVisitLogs.filter(log => {
            const date = log.createdAt ? new Date(log.createdAt) : new Date();
            return date.getHours() === 0; // 00시대
        }).length;

        console.log(`[Achievement] 동기화: 학생 ${myStudents.length}, 심방 ${myVisitLogs.length}, 최대심방 ${maxSingleStudentVisits}`);

        setStats(prev => {
            const newStats = {
                ...prev,
                student_count: Math.max(prev.student_count || 0, myStudents.length),
                teacher_count: Math.max(prev.teacher_count || 0, myTeachers.length),
                visit_count: Math.max(prev.visit_count || 0, myVisitLogs.length),
                schedule_count: Math.max(prev.schedule_count || 0, mySchedules.length),
                finance_count: Math.max(prev.finance_count || 0, myFinances.length),

                // [NEW] 새로 추가된 통계
                max_single_student_visits: Math.max(prev.max_single_student_visits || 0, maxSingleStudentVisits),
                midnight_visit_count: Math.max(prev.midnight_visit_count || 0, midnightVisitCount),
            };

            // ... (기존 심방 유형별 카운트 로직 유지) ...

            const visitTypeCounts = {};
            myVisitLogs.forEach(log => {
                if (log.type) visitTypeCounts[log.type] = (visitTypeCounts[log.type] || 0) + 1;
            });
            Object.keys(visitTypeCounts).forEach(type => {
                newStats.visit_type_counts = newStats.visit_type_counts || {};
                newStats.visit_type_counts[type] = Math.max(
                    newStats.visit_type_counts[type] || 0,
                    visitTypeCounts[type]
                );
            });

            // 저장
            saveToLocal({ earnedBadges, stats: newStats });
            saveToFirestore({ stats: newStats });

            return newStats;
        });

        setTimeout(() => checkAndAwardBadges(), 500);
    }, [earnedBadges, checkAndAwardBadges]);

    // [FIX] 통계가 변경될 때마다 자동으로 배지 조건 체크 (디바운스 적용)
    // 위치 이동: checkAndAwardBadges 함수 정의 후로 이동해야 ReferenceError 방지됨
    useEffect(() => {
        // 초기 로딩 전이거나 데이터가 비어있으면 스킵
        if (!stats.first_access_date && !auth.currentUser) return;

        const timer = setTimeout(() => {
            checkAndAwardBadges();
        }, 500); // 0.5초 대기 후 체크 (연속 업데이트 방지)

        return () => clearTimeout(timer);
    }, [stats, checkAndAwardBadges]);

    return {
        earnedBadges,
        stats,
        incrementStat,
        checkAndAwardBadges,
        awardBadge,
        totalPoints,
        level,
        levelProgress,
        newBadge,
        showBadgeModal,
        closeBadgeModal,
        syncWithExistingData // [NEW] 추가
    };
}
